import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Checkbox, Input, Select } from '../atoms';
import { Modal } from './Modal';
import { useToast } from './Toast';
import { useCurrentUser } from '../lib/useCurrentUser';
import { sanitizeHtml } from '../lib/sanitizeHtml';

/**
 * GenerateEmailModal — the "Generate email" action for the client-facing templates (ATA part 4 §4).
 *
 * Same shape as the savings email: pick the template, the tax year and (for a client) the
 * property subset; pick To and Cc from the client's full contact list, each row carrying its
 * contact role as a small label so an accounting contact is easy to spot; preview the rendered
 * email; create the draft into the email outbox unapproved; then, for an operator whose Outlook
 * is connected (spec 0014), push it to their Outlook Drafts and send it from Outlook; for anyone
 * else, approve (the keystone sends) or reject. A pushed draft is never sent by appealstack.
 *
 *   GET  /api/email-templates
 *   POST /api/email-templates/:slug/preview
 *   POST /api/email-templates/:slug/draft
 *   PUT  /api/email-templates/drafts/:id
 *   POST /api/email-templates/drafts/:id/approve | reject
 *   POST /api/email-templates/drafts/:id/push    (when the draft says push_available)
 *
 * A field the data cannot fill renders as [field name] in the body and is listed above the
 * preview, so the operator fills it before approving instead of sending a blank.
 */

export interface TemplateRow { slug: string; name: string; scope: 'client' | 'appeal'; roles: string[]; where: string; can_generate: boolean }
interface ContactRow { id: string; first_name: string | null; last_name: string | null; email: string | null; role: string | null; is_inherited?: number | boolean }
interface Preview { template: { slug: string; name: string }; subject?: string; body_html?: string; body_text?: string; unresolved: string[]; cc_defaults: string[]; properties: Array<{ id: string; name: string }>; client: { id: string; name: string } | null }
interface Draft { outbox_id: string; to: string[]; cc?: string[]; approved: boolean; status: string; subject?: string; body_html?: string; unresolved?: string[]; push_available?: boolean; channel?: string; provider_web_link?: string | null }

export interface GenerateEmailModalProps {
  open: boolean;
  onClose: () => void;
  /** The record the email is generated from. A property opens the client scope preselected to itself. */
  scope: { entityType: 'clients' | 'appeals'; entityId: string; clientId?: string | null; propertyId?: string | null; label?: string };
  /** Restrict the template list, e.g. the settlement and refund templates on an appeal. */
  slugs?: string[];
}

export function humanRole(role: string | null | undefined): string {
  return (role || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function errMessage(err: unknown): string {
  if (err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body) return String((err.body as { error: unknown }).error);
  return err instanceof Error ? err.message : String(err);
}

export function GenerateEmailModal({ open, onClose, scope, slugs }: GenerateEmailModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const me = useCurrentUser();
  const [slug, setSlug] = useState('');
  const [taxYear, setTaxYear] = useState(String(new Date().getFullYear()));
  const [propertyIds, setPropertyIds] = useState<string[]>(scope.propertyId ? [scope.propertyId] : []);
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [extraTo, setExtraTo] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [done, setDone] = useState<null | 'approved' | 'rejected' | 'pushed'>(null);
  const [outlookLink, setOutlookLink] = useState<string | null>(null);

  const templatesQuery = useQuery<TemplateRow[]>({ queryKey: ['email-templates'], queryFn: ({ signal }) => api.get<TemplateRow[]>('/api/email-templates', signal), enabled: open });
  const templates = useMemo(() => (templatesQuery.data ?? []).filter(t => (t.scope === 'client') === (scope.entityType === 'clients')).filter(t => !slugs || slugs.includes(t.slug)), [templatesQuery.data, scope.entityType, slugs]);
  const clientId = scope.entityType === 'clients' ? scope.entityId : scope.clientId ?? null;
  const contactsQuery = useQuery<ContactRow[]>({ queryKey: ['contacts-by-entity', 'clients', clientId], queryFn: ({ signal }) => api.get<ContactRow[]>(`/api/contacts-by-entity/clients/${clientId}`, signal), enabled: open && !!clientId });
  const propertiesQuery = useQuery<Array<{ id: string; name: string; status?: string }>>({ queryKey: ['client-properties', clientId], queryFn: ({ signal }) => api.get(`/api/properties?client_id=${clientId}&limit=500`, signal), enabled: open && !!clientId && scope.entityType === 'clients' });

  useEffect(() => {
    if (!open) { setSlug(''); setPreview(null); setDraft(null); setDone(null); setOutlookLink(null); setTo([]); setCc([]); setEditing(false); setPropertyIds(scope.propertyId ? [scope.propertyId] : []); }
  }, [open, scope.propertyId]);
  useEffect(() => { if (!slug && templates.length) setSlug(templates.find(t => t.can_generate)?.slug ?? templates[0].slug); }, [templates, slug]);

  const contacts = (contactsQuery.data ?? []).filter(c => c.email);
  const selectedTemplate = templates.find(t => t.slug === slug);
  const toggle = (list: string[], set: (v: string[]) => void, email: string) => set(list.includes(email) ? list.filter(e => e !== email) : [...list, email]);
  const body = () => ({ entity_type: scope.entityType, entity_id: scope.entityId, tax_year: Number(taxYear) || undefined, property_ids: propertyIds.length ? propertyIds : undefined, to, cc });

  const previewMutation = useMutation<Preview, Error, void>({
    mutationFn: () => api.post<Preview>(`/api/email-templates/${slug}/preview`, body()),
    onSuccess: (p) => {
      setPreview(p); setSubject(p.subject ?? ''); setBodyHtml(p.body_html ?? ''); setEditing(false);
      // Cc prefills with the client's Manager and Assistant; the operator can remove them.
      if (cc.length === 0 && p.cc_defaults?.length) setCc(p.cc_defaults);
    },
    onError: (e) => toast({ variant: 'error', message: errMessage(e) }),
  });
  const draftMutation = useMutation<Draft, Error, void>({
    mutationFn: () => api.post<Draft>(`/api/email-templates/${slug}/draft`, { ...body(), subject, body_html: bodyHtml }),
    onSuccess: (d) => { setDraft(d); toast({ variant: 'success', message: 'Draft created. Approve to send it.' }); },
    onError: (e) => toast({ variant: 'error', message: errMessage(e) }),
  });
  const approveMutation = useMutation<Draft, Error, void>({
    mutationFn: () => api.post<Draft>(`/api/email-templates/drafts/${draft!.outbox_id}/approve`, {}),
    onSuccess: () => { setDone('approved'); toast({ variant: 'success', message: `${selectedTemplate?.name ?? 'Email'} approved and sent.` }); queryClient.invalidateQueries({ queryKey: ['activity'] }); },
    onError: (e) => toast({ variant: 'error', message: errMessage(e) }),
  });
  const pushMutation = useMutation<Draft, Error, void>({
    mutationFn: () => api.post<Draft>(`/api/email-templates/drafts/${draft!.outbox_id}/push`, {}),
    onSuccess: (d) => { setDone('pushed'); setOutlookLink(d.provider_web_link ?? null); toast({ variant: 'success', message: 'Draft is in your Outlook Drafts. Review it and send it from Outlook.' }); queryClient.invalidateQueries({ queryKey: ['activity'] }); },
    onError: (e) => toast({ variant: 'error', message: errMessage(e) }),
  });
  const rejectMutation = useMutation<Draft, Error, void>({
    mutationFn: () => api.post<Draft>(`/api/email-templates/drafts/${draft!.outbox_id}/reject`, {}),
    onSuccess: () => { setDone('rejected'); toast({ variant: 'info', message: 'Draft discarded. Nothing was sent.' }); },
    onError: (e) => toast({ variant: 'error', message: errMessage(e) }),
  });
  const busy = previewMutation.isPending || draftMutation.isPending || approveMutation.isPending || rejectMutation.isPending || pushMutation.isPending;
  const blind = !!preview && preview.subject === undefined;

  const addExtra = () => { const e = extraTo.trim(); if (e && !to.includes(e)) setTo([...to, e]); setExtraTo(''); };

  const footer = done ? <Button variant="primary" onClick={onClose}>Close</Button>
    : draft ? (
      <>
        <Button variant="secondary" onClick={() => rejectMutation.mutate()} disabled={busy}>Discard</Button>
        {draft.push_available
          ? <Button variant="primary" onClick={() => pushMutation.mutate()} loading={pushMutation.isPending} disabled={busy}>Push to Outlook Drafts</Button>
          : <Button variant="primary" onClick={() => approveMutation.mutate()} loading={approveMutation.isPending} disabled={busy}>Approve and send</Button>}
      </>
    ) : preview ? (
      <>
        <Button variant="secondary" onClick={() => setPreview(null)} disabled={busy}>Back</Button>
        <Button variant="primary" onClick={() => draftMutation.mutate()} loading={draftMutation.isPending} disabled={busy || to.length === 0}>Create draft</Button>
      </>
    ) : (
      <>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={() => previewMutation.mutate()} loading={previewMutation.isPending} disabled={busy || !slug || !selectedTemplate?.can_generate}>Preview</Button>
      </>
    );

  const label = { display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' } as const;
  const hint = { fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' } as const;

  return (
    <Modal open={open} onClose={onClose} title={done ? (done === 'approved' ? 'Email sent' : done === 'pushed' ? 'In your Outlook Drafts' : 'Draft discarded') : draft ? 'Review the draft' : preview ? `Preview: ${preview.template.name}` : 'Generate email'} size="lg" closeOnBackdropClick={!busy}>
      {done && done !== 'pushed' && <p style={{ margin: 0 }}>{done === 'approved' ? `The ${selectedTemplate?.name ?? 'email'} was approved and handed to the sender. It is recorded on this record's activity.` : 'The draft was discarded and nothing was sent.'}</p>}
      {done === 'pushed' && (
        <p style={{ margin: 0 }}>
          The {selectedTemplate?.name ?? 'email'} is in your Outlook Drafts with your signature. Open it, make any last changes and press Send in Outlook; appealstack has not sent it.{' '}
          {outlookLink && <a href={outlookLink} target="_blank" rel="noreferrer">Open in Outlook</a>}
        </p>
      )}

      {!done && !preview && (
        <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
          <div>
            <span style={label}>Template</span>
            <Select aria-label="Template" value={slug} onChange={(e) => setSlug(e.target.value)} options={templates.map(t => ({ value: t.slug, label: t.can_generate ? t.name : `${t.name} (generated by ${t.roles.map(humanRole).join(', ')})` }))} />
            {selectedTemplate && <div style={{ ...hint, marginTop: 'var(--sp-1)' }}>{selectedTemplate.where}</div>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)', gap: 'var(--sp-3)' }}>
            <div>
              <span style={label}>Tax year</span>
              <Input aria-label="Tax year" type="number" value={taxYear} onChange={(e) => setTaxYear(e.target.value)} />
            </div>
            {scope.entityType === 'clients' && (
              <div>
                <span style={label}>Properties {propertyIds.length ? `(${propertyIds.length} selected)` : '(all active)'}</span>
                <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-2)', display: 'grid', gap: 'var(--sp-1)' }}>
                  {(propertiesQuery.data ?? []).map(p => (
                    <Checkbox key={p.id} label={p.name} checked={propertyIds.includes(p.id)} onChange={() => toggle(propertyIds, setPropertyIds, p.id)} />
                  ))}
                  {propertiesQuery.data && propertiesQuery.data.length === 0 && <span style={hint}>This client has no properties yet.</span>}
                </div>
                <div style={{ ...hint, marginTop: 'var(--sp-1)' }}>Leave every box clear to include all active properties.</div>
              </div>
            )}
          </div>
          <div>
            <span style={label}>To</span>
            <div data-testid="to-picker" style={{ display: 'grid', gap: 'var(--sp-1)' }}>
              {contacts.map(c => (
                <Checkbox key={`to-${c.id}`} checked={to.includes(c.email!)} onChange={() => toggle(to, setTo, c.email!)}
                  label={<span style={{ display: 'inline-flex', gap: 'var(--sp-2)', alignItems: 'center' }}>{[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email} <span style={hint}>{c.email}</span> {c.role && <Badge variant="neutral">{humanRole(c.role)}</Badge>}</span>} />
              ))}
              {contactsQuery.data && contacts.length === 0 && <span style={hint}>No client contact has an email on file. Add one below.</span>}
              <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                <Input aria-label="Add another recipient" type="email" placeholder="someone@client.com" value={extraTo} onChange={(e) => setExtraTo(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExtra(); } }} />
                <Button variant="secondary" size="sm" onClick={addExtra} disabled={!extraTo.trim()}>Add</Button>
              </div>
              {to.filter(e => !contacts.some(c => c.email === e)).map(e => <Checkbox key={`to-x-${e}`} checked onChange={() => toggle(to, setTo, e)} label={e} />)}
            </div>
          </div>
          <div>
            <span style={label}>Cc</span>
            <div data-testid="cc-picker" style={{ display: 'grid', gap: 'var(--sp-1)' }}>
              {contacts.map(c => (
                <Checkbox key={`cc-${c.id}`} checked={cc.includes(c.email!)} onChange={() => toggle(cc, setCc, c.email!)}
                  label={<span style={{ display: 'inline-flex', gap: 'var(--sp-2)', alignItems: 'center' }}>{[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email} <span style={hint}>{c.email}</span> {c.role && <Badge variant="neutral">{humanRole(c.role)}</Badge>}</span>} />
              ))}
              <span style={hint}>The client&apos;s Manager and Assistant are added to Cc when you preview; remove them on the next screen if you prefer.</span>
            </div>
          </div>
        </div>
      )}

      {!done && preview && (
        <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
          {preview.unresolved.length > 0 && !blind && (
            <div role="status" style={{ padding: 'var(--sp-2) var(--sp-3)', borderRadius: 'var(--r-md)', background: 'var(--color-bg)', border: '1px solid var(--color-border)', fontSize: 'var(--fs-sm)' }}>
              <strong>Fill before sending:</strong> {preview.unresolved.map(k => `[${k.split('.').pop()!.replace(/_usd$/, '').replace(/_/g, ' ')}]`).join(', ')}. appealstack has no value for these yet; they show in brackets in the body.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--sp-1) var(--sp-3)', fontSize: 'var(--fs-sm)' }}>
            <span style={hint}>To</span><span>{(draft?.to ?? to).join(', ')}</span>
            <span style={hint}>Cc</span>
            <span style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-1)' }}>
              {(draft?.cc ?? cc).length === 0 && <span style={hint}>none</span>}
              {(draft?.cc ?? cc).map(e => <Badge key={e} variant="neutral">{e}{!draft && <button type="button" aria-label={`Remove ${e} from Cc`} onClick={() => setCc(cc.filter(x => x !== e))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', marginLeft: 4 }}>×</button>}</Badge>)}
            </span>
          </div>
          {blind ? (
            <p style={hint}>Your role does not see the dollar figures this email contains. You can still create the draft for a colleague to review.</p>
          ) : (
            <>
              <div>
                <span style={label}>Subject</span>
                <Input aria-label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!!draft} />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={label}>Body</span>
                  {!draft && <button type="button" onClick={() => setEditing(!editing)} style={{ border: 'none', background: 'transparent', color: 'var(--brand-primary)', cursor: 'pointer', fontSize: 'var(--fs-xs)' }}>{editing ? 'Show preview' : 'Edit HTML'}</button>}
                </div>
                {editing ? (
                  <textarea aria-label="Body HTML" value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} rows={18} style={{ width: '100%', fontFamily: 'var(--ff-mono, monospace)', fontSize: 'var(--fs-xs)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-2)' }} />
                ) : (
                  <div data-testid="email-preview" style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-4)', background: '#fff', color: '#111', maxHeight: 420, overflow: 'auto' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(draft?.body_html ?? bodyHtml) }} />
                )}
              </div>
            </>
          )}
          {draft && !draft.push_available && <p style={hint}>Draft {draft.outbox_id.slice(0, 8)} is waiting in the outbox. Approving hands it to the sender as {me?.email ?? 'you'}; discarding removes it.</p>}
          {draft && draft.push_available && <p style={hint}>Pushing puts this draft in your own Outlook Drafts, where you review it and send it yourself. Discarding removes it.</p>}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)', marginTop: 'var(--sp-5)' }}>{footer}</div>
    </Modal>
  );
}
